/**
 * AuthProvider - Token lifecycle management
 *
 * Handles:
 * 1. Token hydration from persisted storage on app boot
 * 2. Proactive token refresh before expiration
 * 3. Visibility-based refresh when tab becomes active
 * 4. Graceful session expiration handling
 */

import { createContext, useContext, useEffect, useRef, useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { syncAuthStateFromStorageValue, useAuthStore } from '@/store/auth-store';
import { api } from '@/lib/api';
import { AUTH_CONFIG } from '@/lib/constants';
import { shouldBypassAuthLifecycle } from '@/lib/auth-lifecycle';
import { env } from '@/lib/env';

interface AuthContextValue {
  isInitialized: boolean;
  isRefreshing: boolean;
  lastRefreshTime: number | null;
  refreshToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState<number | null>(null);

  const location = useLocation();
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastRefreshAttemptRef = useRef<number>(0);
  const hasValidatedStartupAuthRef = useRef(false);

  const { token, refreshToken: storedRefreshToken, logout, hasHydrated } = useAuthStore();
  const bypassAuthLifecycle = shouldBypassAuthLifecycle(location.pathname);

  // Use refs to break circular dependency between performRefresh and scheduleNextRefresh
  const performRefreshRef = useRef<(() => Promise<boolean>) | undefined>(undefined);
  const scheduleNextRefreshRef = useRef<(() => void) | undefined>(undefined);

  const clearScheduledRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  }, []);

  /**
   * Schedule the next proactive refresh
   */
  const scheduleNextRefresh = useCallback(() => {
    clearScheduledRefresh();

    // There is the chance of a null token at initial hydration, immediately after logout,
    // or right after cross-tab storage sync clears auth state.
    if (!token || bypassAuthLifecycle) return;

    // Calculate when to refresh (at REFRESH_THRESHOLD of lifetime)
    const refreshIn = AUTH_CONFIG.TOKEN_LIFETIME_MS * AUTH_CONFIG.REFRESH_THRESHOLD;

    refreshTimeoutRef.current = setTimeout(() => {
      performRefreshRef.current?.();
    }, refreshIn);
  }, [clearScheduledRefresh, token, bypassAuthLifecycle]);

  /**
   * Perform a token refresh
   */
  const performRefresh = useCallback(async (): Promise<boolean> => {
    if (bypassAuthLifecycle) {
      return false;
    }

    const now = Date.now();

    // Prevent rapid refresh attempts
    if (now - lastRefreshAttemptRef.current < AUTH_CONFIG.MIN_REFRESH_INTERVAL_MS) {
      return false;
    }

    const currentRefreshToken = storedRefreshToken || token;
    if (!currentRefreshToken) {
      return false;
    }

    lastRefreshAttemptRef.current = now;
    setIsRefreshing(true);

    try {
      const newToken = await api.refreshAuthToken();
      if (newToken) {
        setLastRefreshTime(Date.now());
        scheduleNextRefreshRef.current?.();
        return true;
      }
      return false;
    } catch (error) {
      // Only logout if this was a definitive auth failure, not a network error
      if (error instanceof Error && error.message.includes('session expired')) {
        logout();
      }
      return false;
    } finally {
      setIsRefreshing(false);
    }
  }, [storedRefreshToken, token, logout, bypassAuthLifecycle]);

  // Keep refs in sync with performRefresh and scheduleNextRefresh
  useEffect(() => {
    performRefreshRef.current = performRefresh;
    scheduleNextRefreshRef.current = scheduleNextRefresh;
  }, [performRefresh, scheduleNextRefresh]);

  const validateStoredToken = useCallback(async () => {
    if (!AUTH_CONFIG.VALIDATE_TOKEN_ON_STARTUP || bypassAuthLifecycle || !token) {
      return;
    }

    const initialToken = token;
    const initialRefreshToken = storedRefreshToken;

    api.setAuthToken(initialToken);
    api.setRefreshToken(initialRefreshToken || initialToken);

    const isTokenStillCurrent = (expectedToken: string) =>
      useAuthStore.getState().token === expectedToken;

    const fetchUser = async (accessToken: string) => {
      return fetch(`${env.apiBaseUrl}/users/me`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });
    };

    try {
      const response = await fetchUser(initialToken);

      if (response.status === 401 && initialRefreshToken) {
        let refreshedToken: string | null = null;
        try {
          refreshedToken = await api.refreshAuthToken();
          const retryResponse = await fetchUser(refreshedToken);

          if (!retryResponse.ok) {
            throw new Error('Token refresh validation failed');
          }

          const refreshedUserData = await retryResponse.json();
          if (refreshedUserData && useAuthStore.getState().token === refreshedToken) {
            useAuthStore.getState().updateUser(refreshedUserData);
            setLastRefreshTime(Date.now());
            if (env.features.enableDebugLogging) {
              console.log('🔐 Token refreshed successfully on startup');
            }
          }
          return;
        } catch {
          const currentToken = useAuthStore.getState().token;
          if (currentToken === initialToken || currentToken === refreshedToken) {
            logout();
          }
          return;
        }
      }

      if (!response.ok) {
        if (env.features.enableDebugLogging) {
          console.log('🔐 Stored token is invalid/expired, clearing auth state');
        }
        if (isTokenStillCurrent(initialToken)) {
          logout();
        }
        return;
      }

      const userData = await response.json();
      if (userData && isTokenStillCurrent(initialToken)) {
        useAuthStore.getState().updateUser(userData);
        if (env.features.enableDebugLogging) {
          console.log('🔐 Token validated successfully, user data refreshed');
        }
      }
    } catch (error) {
      if (env.features.enableDebugLogging) {
        console.warn('🔐 Token validation failed due to network error:', error);
      }
      if (AUTH_CONFIG.LOGOUT_ON_NETWORK_ERROR && isTokenStillCurrent(initialToken)) {
        logout();
      }
    }
  }, [bypassAuthLifecycle, logout, storedRefreshToken, token]);

  /**
   * Handle visibility change - refresh if tab was hidden for a while
   */
  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState !== 'visible') return;
    if (bypassAuthLifecycle || !token) return;

    const timeSinceLastRefresh = lastRefreshTime ? Date.now() - lastRefreshTime : Infinity;

    // If it's been a while since last refresh, refresh now
    if (timeSinceLastRefresh > AUTH_CONFIG.VISIBILITY_REFRESH_THRESHOLD_MS) {
      performRefresh();
    }
  }, [token, lastRefreshTime, performRefresh, bypassAuthLifecycle]);

  /**
   * Keep the API client aligned with the hydrated auth store state.
   */
  useEffect(() => {
    if (!hasHydrated) {
      return;
    }

    if (!token) {
      clearScheduledRefresh();
      api.clearTokens();
      setLastRefreshTime(null);
    } else {
      api.setAuthToken(token);
      api.setRefreshToken(storedRefreshToken || token);
    }
  }, [clearScheduledRefresh, hasHydrated, storedRefreshToken, token]);

  /**
   * Wait for Zustand hydration to complete before initializing
   * This prevents race conditions where API requests are made before tokens are loaded from localStorage
   */
  useEffect(() => {
    if (!hasHydrated) {
      return;
    }

    clearScheduledRefresh();

    if (bypassAuthLifecycle) {
      setLastRefreshTime(null);
      setIsInitialized(true);
      return;
    }

    if (token) {
      setLastRefreshTime(Date.now());
      scheduleNextRefresh();
      if (!hasValidatedStartupAuthRef.current) {
        hasValidatedStartupAuthRef.current = true;
        void validateStoredToken();
      }
    } else {
      hasValidatedStartupAuthRef.current = true;
      setLastRefreshTime(null);
    }

    setIsInitialized(true);
  }, [
    bypassAuthLifecycle,
    clearScheduledRefresh,
    hasHydrated,
    scheduleNextRefresh,
    token,
    validateStoredToken,
  ]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.storageArea !== window.localStorage || event.key !== AUTH_CONFIG.STORAGE_KEY) {
        return;
      }

      syncAuthStateFromStorageValue(event.newValue);

      const syncedState = useAuthStore.getState();
      if (!syncedState.token) {
        clearScheduledRefresh();
        setLastRefreshTime(null);
        return;
      }

      setLastRefreshTime(Date.now());
      scheduleNextRefreshRef.current?.();
    };

    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [clearScheduledRefresh]);

  /**
   * Set up visibility change listener
   */
  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleVisibilityChange]);

  /**
   * Clean up timeout on unmount
   */
  useEffect(() => {
    return () => {
      clearScheduledRefresh();
    };
  }, [clearScheduledRefresh]);

  const contextValue: AuthContextValue = {
    isInitialized,
    isRefreshing,
    lastRefreshTime,
    refreshToken: performRefresh,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}
